<template>
  <div>
    <b-input
      v-model="departmentCredentials.name"
      :error-messages="getErrorMessages(v$.name)"
      label="Назва"
      placeholder="введіть назву департаменту"
      @blur="v$.name.$touch()"></b-input>
    <b-select
      v-model="departmentCredentials.parentId"
      :items="[{ id: 0, name: 'Ні' }, ...$directory.get('departmentList')]"
      :loading="$loading.isLoading('departmentList')"
      label="Відноситься до департаменту"></b-select>

    <div v-if="dialog.action === 'update'">
      <v-divider class="mb-4"></v-divider>
      <div class="d-flex align-center ga-2">
        <div
          class="text-disabled my-2 bg-blue-grey-lighten-5 d-inline-flex align-center gap-1"
          style="width: fit-content; padding: 2px">
          <v-icon size="x-small">mdi-account-tie</v-icon>
          <span class="font-weight-bold text-black font-size-14">Керівник</span>
        </div>
        <span>
          {{ getPersonName(departmentSource.supervisor?.user) || 'Не вказано' }}
        </span>
        <v-btn
          v-if="departmentSource.supervisor"
          v-tooltip="'Видалити керівника'"
          :loading="$loading.isLoading('userUnsetSupervisor')"
          color="error"
          density="comfortable"
          icon="mdi-delete"
          size="small"
          variant="plain"
          @click="supervisorHandler(departmentSource.supervisor)">
        </v-btn>
      </div>

      <v-divider class="mt-4 mb-4"></v-divider>

      <div class="d-flex align-center justify-space-between">
        <div
          class="text-disabled my-2 bg-blue-grey-lighten-5 d-inline-flex align-center gap-1"
          style="width: fit-content; padding: 2px">
          <v-icon size="x-small">mdi-account-group-outline</v-icon>
          <span class="font-weight-bold text-black font-size-14"
            >Співробітники</span
          >
        </div>
        <v-btn
          border="sm"
          class="mt-3 font-weight-bold text-none"
          color="white"
          rounded="lg"
          @click="userAddToDepartment">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Додати
        </v-btn>
      </div>

      <v-data-table
        :headers="departmentUserListHeaders"
        :items="departmentSource.employees"
        :items-per-page="-1"
        hide-default-footer
        item-value="id">
        <template #item.name="{ item }">
          <button
            class="link"
            type="button"
            @click="openEmployeeCard(item.user)">
            {{ getPersonName(item.user) }}
          </button>
        </template>
        <template #item.isActive="{ item }">
          <v-chip
            :color="item.user.isActive ? 'success' : 'error'"
            size="small">
            {{ item.user.isActive ? 'Активний' : 'Звільнений' }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <b-action-menu
            :actions="[
              {
                title:
                  item.id === departmentSource.supervisor?.user.id
                    ? 'Відкликати супервайзера'
                    : 'Призначити супервайзером',
                icon: 'mdi-account-tie',
                action: () => supervisorHandler(item),
                iconProps: {
                  color: 'blue-darken-3',
                },
              },
              {
                title: 'Видалити з департаменту',
                icon: 'mdi-delete',
                action: () => userRemoveFromDepartment(item.id),
                iconProps: {
                  color: 'error',
                },
              },
            ]"></b-action-menu>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script lang="ts">
import { reactive } from 'vue'
import {
  cachedObject,
  getErrorMessages,
  getPersonName,
} from 'best-modules/utils'
import {
  Department,
  DepartmentCredentials,
  DepartmentUser,
} from '@/utils/types/departments'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import axios from '@/plugins/axios'
import {
  urlDepartmentAddUser,
  urlDepartmentCreate,
  urlDepartmentRemoveUser,
  urlDepartmentSetSupervisor,
  urlDepartmentUpdate,
} from '@/utils/urls'
import { fillDirectory } from '@/directory'
import { BActionMenu } from 'best-modules/components'
import { departmentUserListHeaders } from '@/utils/tableHeaders'
import { openDialog } from '@/dialog'
import { User } from '@/utils/types/user'
import { cloneDeep, omit, pick } from 'lodash'
import { openUserCardDialog } from '@/utils/userCardDialog'

export default {
  name: 'Department',
  components: { BActionMenu },
  props: {
    dialog: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { departmentUserListHeaders }
  },
  methods: { getPersonName, getErrorMessages },
  setup(props) {
    fillDirectory('departmentList')

    const departmentCredentials = cachedObject<DepartmentCredentials>({
      name: null,
      parentId: props.dialog.dialogData.department.parentId,
    } as Department)
    departmentCredentials.$setIgnore('employees', 'supervisor', 'isActive')

    const departmentSource = reactive<Department>(
      cloneDeep(props.dialog.dialogData.department) as Department
    )

    if (props.dialog.action === 'update') {
      departmentCredentials.$set(
        pick(props.dialog.dialogData.department, ['name', 'parentId'])
      )
    }

    const rules = {
      name: { required },
    }
    const v$ = useVuelidate(rules, departmentCredentials)

    const submit = () => {
      if (props.dialog.action === 'create') {
        return axios.post(urlDepartmentCreate(), departmentCredentials.$object)
      }

      if (
        props.dialog.action === 'update' &&
        departmentCredentials.$hasChanges
      ) {
        return axios.put(
          urlDepartmentUpdate(props.dialog.dialogData.department.id),
          departmentCredentials.$getChangedFields()
        )
      }
      if (
        props.dialog.action === 'update' &&
        !departmentCredentials.$hasChanges
      ) {
        return Promise.resolve(null)
      }
    }

    const userAddToDepartment = () => {
      openDialog({
        name: 'UserSelect',
        params: {
          cb: (user: User) => {
            if (props.dialog.action === 'update') {
              axios
                .post(urlDepartmentAddUser(), {
                  departmentId: props.dialog.dialogData.department.id,
                  userId: user.id,
                  isSupervisor: false,
                })
                .then(res => {
                  if (props.dialog.action === 'update') {
                    departmentSource.employees.push(
                      omit(res.data, 'department') as DepartmentUser<'user'>
                    )
                    props.dialog.dialogData.updateItem(departmentSource)
                  }
                })
            }
          },
        },
      })
    }
    const userRemoveFromDepartment = (userId: number) => {
      return axios.delete(urlDepartmentRemoveUser(userId)).then(() => {
        if (props.dialog.action === 'update') {
          departmentSource.employees = departmentSource.employees.filter(
            u => u.id !== userId
          )
          props.dialog.dialogData.updateItem(departmentSource)
        }
      })
    }

    const supervisorHandler = async (user: DepartmentUser<'user'>) => {
      if (props.dialog.action === 'update') {
        const isSupervisor = departmentSource.supervisor?.id !== user.id

        const res = await axios.patch(urlDepartmentSetSupervisor(user.id), {
          isSupervisor,
        })
        if (isSupervisor) {
          departmentSource.supervisor = res.data
          departmentSource.employees = departmentSource.employees.filter(
            u => u.id !== res.data.id
          )
        } else {
          departmentSource.supervisor = null as any
          departmentSource.employees.push(res.data)
        }
        props.dialog.dialogData.updateItem(departmentSource)
      }
    }

    const openEmployeeCard = (user: User) => {
      openUserCardDialog({
        userId: user.id,
        mode: 'employee',
        context: 'admin',
        title: getPersonName(user),
        cb: () => {
          props.dialog.dialogData.updateItem(departmentSource)
        },
      })
    }

    return {
      departmentCredentials,
      departmentSource,
      v$,
      submit,
      userRemoveFromDepartment,
      userAddToDepartment,
      supervisorHandler,
      openEmployeeCard,
    }
  },
}
</script>

<style lang="scss" scoped>
.link {
  background: transparent;
  border: 0;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
</style>
